import {HttpErrorResponse} from '@angular/common/http';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  Optional,
  Output,
  SimpleChanges,
} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {OverlayService} from '@aztrix/components/overlay';
import {BASE_URL} from '@aztrix/environment';
import {getLanguageForTimestamp, hasVerification, isOwner} from '@aztrix/helpers';
import {
  AgreementDataRepresentation,
  AgreementRepresentation,
  ProfileRepresentation,
  PropertyRepresentation,
  ProposalLanguageRepresentation,
  ProposalLanguagesService,
  ProposalRepresentation,
} from '@aztrix/sdk';
import {ConfirmExpireModalComponent} from '@aztrix/subscribe/confirm-expire-modal';
import {combineLatest, Observable, of, ReplaySubject} from 'rxjs';
import {filter, first, map, mergeMap, shareReplay, startWith, switchMap} from 'rxjs/operators';

@Component({
  selector: 'ax-agreement-view',
  templateUrl: './agreement-view.component.html',
  styleUrls: ['./agreement-view.component.scss', './property.scss'],
})
export class AgreementViewComponent implements OnChanges {
  @Input() myProfile?: ProfileRepresentation;
  @Input() proposal: ProposalRepresentation | undefined;
  @Input() agreement: AgreementRepresentation | undefined;
  @Input() agreementData: AgreementDataRepresentation | undefined;

  @Input() historyData: AgreementDataRepresentation[];
  @Input() canExpire = true;
  @Input() isExternal = false;
  @Input() legal = true;
  @Input() autoVerification = true;
  @HostBinding('class.compact') @Input() compact = false;

  @Output() didExpire = new EventEmitter<AgreementRepresentation>();
  @Output() addProperty = new EventEmitter<PropertyRepresentation>();
  @Output() editProperty = new EventEmitter<PropertyRepresentation | undefined>();
  @Output() didClickSendEmail = new EventEmitter();

  @Output() verified = new EventEmitter<AgreementRepresentation | undefined>();
  @Output() verificationError = new EventEmitter<HttpErrorResponse>();
  @Output() done = new EventEmitter<void>();
  @Output() resendSuccess = new EventEmitter<AgreementRepresentation>();

  // eslint-disable-next-line @typescript-eslint/naming-convention
  AgreementVerificationType = ProposalRepresentation.VerificationEnum;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  PropertyType = PropertyRepresentation.TypeEnum;

  canSubscribe: boolean;

  showSuccessMessage = false;

  private _proposal$ = new ReplaySubject<ProposalRepresentation>(1);
  private _agreement$ = new ReplaySubject<AgreementRepresentation>(1);
  private _agreementData$ = new ReplaySubject<AgreementDataRepresentation>(1);
  private _myProfile$ = new ReplaySubject<ProfileRepresentation>(1);
  private _canExpire$ = new ReplaySubject<boolean>(1);

  private _proposalLanguages$: Observable<ProposalLanguageRepresentation[]> = combineLatest([
    this._proposal$,
    this._agreement$,
  ]).pipe(
    switchMap(([proposal, agreement]) => {
      // proposal languages for deleted agreements
      if (agreement.deletedByOwner && proposal?.languages && proposal?.languages?.length > 0) {
        return of(proposal.languages || []);
      }
      return this._getProposalLanguages(proposal.id || '', agreement.agreementCode);
    }),
    shareReplay(1)
  );

  isHistoryAgreement$: Observable<boolean> = combineLatest([
    this._agreement$,
    this._agreementData$.pipe(filter((d) => !!d)),
  ]).pipe(
    map(
      ([agreement, agreementData]) =>
        agreementData.startTimestamp !== agreement.agreementData?.startTimestamp
    )
  );

  language$: Observable<ProposalLanguageRepresentation> = combineLatest([
    this._proposalLanguages$,
    this._agreementData$.pipe(startWith(null)),
  ]).pipe(
    map(
      ([languages, agreementData]) =>
        <ProposalLanguageRepresentation>(
          getLanguageForTimestamp(languages, agreementData?.language, agreementData?.startTimestamp)
        )
    ),
    shareReplay(1)
  );

  successMessage$ = this.language$.pipe(
    map((language) => {
      return language.successMessage;
    })
  );

  pdfDownloadEnabled$ = this.language$.pipe(
    map((proposalLanguage) => {
      return proposalLanguage.pdfDownloadEnabled;
    })
  );

  whiteLabel$ = this.language$.pipe(map((l) => !!l?.whiteLabel));

  startTimestamp$ = this._agreementData$.pipe(
    filter((d) => !!d),
    map((d) => d.startTimestamp)
  );

  verificationTimestamp$ = this._agreement$.pipe(map((d) => d.verificationTimestamp));

  canViewHistory$: Observable<boolean> = combineLatest([
    this._agreementData$,
    this._agreement$,
  ]).pipe(map(([agreementData, agreement]) => !!agreementData || !!agreement.expirationTimestamp));

  useRouterLink = window.location.origin.includes(this.baseUrl);

  isSubscriber$: Observable<boolean> = combineLatest([this._myProfile$, this._agreement$]).pipe(
    map(([myProfile, agreement]) => myProfile.id === agreement.subscriberId)
  );

  createdByIsMyProfile$ = combineLatest([this._myProfile$, this._agreementData$]).pipe(
    mergeMap(([myProfile, agreementData]) => {
      if (!myProfile) {
        return of(false);
      } else if (agreementData?.createdBy === 'OWNER') {
        return this._proposal$.pipe(map((proposal) => proposal.ownerId === myProfile.id));
      } else if (agreementData?.createdBy === 'SUBSCRIBER') {
        return this._agreement$.pipe(map((agreement) => agreement.subscriberId === myProfile.id));
      } else {
        return of(false);
      }
    })
  );

  isProposalOwner$: Observable<boolean> = combineLatest([this._agreement$, this._myProfile$]).pipe(
    map(([agreement, profile]) => !!this.myProfile && isOwner(agreement, profile))
  );

  canViewData$: Observable<boolean> = combineLatest([
    this._agreement$,
    this.isProposalOwner$.pipe(startWith(false)),
  ]).pipe(
    map(([agreement, proposalOwner]) => {
      if (proposalOwner) {
        return (
          (!agreement.deletedByOwner && !this.isExternal) ||
          (this.isExternal && !agreement.deletedByOwner)
        );
      } else {
        return (
          (agreement.deletedByOwner && !this.isExternal) ||
          (!agreement.deletedByOwner && this.isExternal) ||
          (!agreement.deletedByOwner && !this.isExternal)
        );
      }
    })
  );

  canExpire$ = combineLatest([this._agreement$, this._canExpire$]).pipe(
    map(([agreement, canExpire]) => !agreement?.expirationTimestamp && canExpire)
  );

  isVerified$ = this._agreement$.pipe(map((agreement) => agreement.verified || false));
  isActive$ = this._agreement$.pipe(map((agreement) => agreement.active || false));

  constructor(
    private _overlay: OverlayService,
    public dialog: MatDialog,
    private _elementRef: ElementRef,
    private _proposalLanguagesService: ProposalLanguagesService,
    @Optional() @Inject(BASE_URL) public baseUrl: string
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (this.agreement && changes.agreement) {
      this._agreement$.next(this.agreement);
    }

    if (this.proposal && changes.proposal) {
      this._proposal$.next(this.proposal);
    }

    if (this.agreementData && changes.agreementData) {
      this._agreementData$.next(this.agreementData);
    }

    if (this.myProfile && changes.myProfile) {
      this._myProfile$.next(this.myProfile);
    }

    if (changes.canExpire) {
      this._canExpire$.next(this.canExpire);
    }

    this.canSubscribe = this.proposal?.profileContext === this.myProfile?.profileContext;
  }

  get proposalHasVerification(): boolean {
    return hasVerification(this.proposal);
  }

  expire(): void {
    this._overlay.createModal(this._elementRef, ConfirmExpireModalComponent, {
      title: 'agreement.modal.expire.title',
      init: (expireModal) => {
        expireModal.agreement = <AgreementRepresentation>this.agreement;
        expireModal.expired.pipe(first()).subscribe((updatedAgreement) => {
          this.didExpire.emit(updatedAgreement);
        });
      },
    });
  }

  actionAfterSuccessConfirm() {
    this.showSuccessMessage = false;
    this.done.emit();
  }

  actionAfterVerification(agreement: AgreementRepresentation) {
    if (agreement) {
      this._agreement$.next(agreement);
      this.verified.emit(agreement);
    }

    this.language$.pipe(first()).subscribe((language) => {
      if (language?.redirectUrl) {
        this.done.emit();
        window.location.href = language.redirectUrl;
      }
      if (language.successMessage) {
        this.showSuccessMessage = true;
      }

      if (!this.showSuccessMessage && !language?.redirectUrl) {
        this.done.emit();
      }
    });
  }

  private _getProposalLanguages(proposalId: string, agreementCode?: string) {
    return this._proposalLanguagesService.getLanguagesHistory(proposalId, agreementCode);
  }
}
